/**
 * Data model for recommendation widgets containing configuration and product data.
 */
export class RecommendationModel {
  /**
   * Creates a new RecommendationModel instance with the provided properties.
   *
   * @param properties - Complete configuration and data for the recommendation
   * widget, including widget metadata, recommendation type, page context, products,
   * and optional design settings. See {@link RecommendationModelProperties} for
   * detailed property descriptions.
   */
  constructor(public properties: RecommendationModelProperties) {}
}

export interface RecommendationModelProperties {
  widgetName: string;
  widgetId: string;
  recommendationType:
    | 'bestsellers'
    | 'newest-arrivals'
    | 'frequently-bought-together'
    | 'trending-products'
    | 'hand-picked-products'
    | 'recently-viewed'
    | 'most-viewed'
    | 'related-items';
  widgetDesignSettings?: WidgetDesignSettings;
  page: SupportedPage;
  products: Product[];
  /**
   * Optional rule groups for rule-based recommendations.
   * Only used by 'hand-picked-products' and 'bestsellers' recommendation types.
   * Backend transforms raw data (IDs, strings) into UI-friendly format with labels.
   */
  rules?: RuleGroupType[];
}

export type SupportedPage =
  | 'home-page'
  | 'cart-page'
  | 'collection-page'
  | 'product-page'
  | 'cart-drawer';

export interface SelectedOption {
  id: string | number;
  title: string;
  originPrice: number;
  price: string;
}

export type ExtendedRecommendationProduct = Product & {
  checked: boolean;
  selectedOption: SelectedOption;
};

export type ExtendedRecommendationModelProperties = RecommendationModelProperties & {
  totalPrice: string;
  hasSelectedProduct: boolean;
};

export type RecommendationProduct = ExtendedRecommendationProduct;

export interface ProductItemTranslation {
  atcAddingToCartBtnLabel?: string;
  atcAddedToCartBtnLabel?: string;
  atcFailedToCartBtnLabel?: string;
}

export interface AppTranslation {
  productItem?: ProductItemTranslation;
}

// ============================================================================
// Rule-Based Recommendation Types
// ============================================================================

/**
 * Product information in simplified format for rule conditions and consequences.
 * Used when backend transforms product IDs into UI-friendly format.
 */
export interface ProductResponseType {
  id: string;
  title: string;
  images: string[];
}

/**
 * Collection information with label/value structure for UI display.
 * Used in rule conditions and consequences for collection filters.
 */
export interface CollectionResponseType {
  value: string; // Collection ID
  label: string; // Collection title
}

/**
 * Condition type for rule-based recommendations.
 * Defines the "IF" part of a rule - what criteria must be met.
 *
 * @remarks
 * The value field is polymorphic based on the option:
 * - 'productId': Array<number> (raw) or Array<ProductResponseType> (transformed by backend)
 * - 'collectionId': Array<CollectionResponseType> (always with label)
 * - 'vendor'/'tag'/'product-type': Array<string> (raw) or Array<{label, value}> (UI-friendly)
 * - 'price': number or Array with first element
 */
export interface ConditionType {
  option:
    | 'all-products'
    | 'all-collections'
    | 'algorithm'
    | 'reviewsio-rating'
    | 'total-reviewsio-rating'
    | 'productId'
    | 'vendor'
    | 'product-category'
    | 'collectionId'
    | 'price'
    | 'tag'
    | 'product-type';
  conductor?:
    | 'equal'
    | 'not_equal'
    | 'greater_than'
    | 'greater_equal_than'
    | 'less_than'
    | 'less_equal_than'
    | 'contains'
    | 'equal_collection'
    | 'not_equal_collection'
    | 'equal_product_category'
    | 'not_equal_product_category'
    | 'equal_product_type'
    | 'not_equal_product_type'
    | 'equal_vendor'
    | 'not_equal_vendor'
    | 'contains_all_tags'
    | 'contains_matched_tags';
  value?:
    | string
    | number
    | string[]
    | { label: string; value: string }[]
    | number[]
    | ProductResponseType[]
    | CollectionResponseType[];
}

/**
 * Consequence type for rule-based recommendations.
 * Defines the "THEN" part of a rule - what products to show when conditions are met.
 *
 * @remarks
 * Has identical structure to ConditionType. Backend uses buildRuleFieldResponse()
 * to transform raw data (IDs, strings) into UI-friendly format with labels.
 */
export type ConsequenceType = ConditionType;

/**
 * Rule group combining conditions and consequences.
 * Represents a complete rule: IF conditions THEN show consequences.
 *
 * @remarks
 * Used in 'hand-picked-products' and 'bestsellers' recommendation types.
 * Backend enforces a limit of 150 rule groups per widget.
 */
export interface RuleGroupType {
  conditions: ConditionType[];
  consequences: ConsequenceType[];
}

// ============================================================================
// Widget Design Settings
// ============================================================================

interface WidgetDesignSettings {
  layoutDisplay?: 'carousel' | 'grid' | 'bundle';
  numberOfProductPerRow?: number;
  /** Number of products per row for slide cart (side cart) display - specific to cart-drawer */
  numberOfProductPerRowSlideCart?: number;
  /** Number of products per row for popup cart (modal cart) display - specific to cart-drawer */
  numberOfProductPerRowPopupCart?: number;
  numberOfRecommendProduct?: number;
  bundleStyle?: 'style1' | 'style2';
  templateType?: 'default' | 'customization';
  themePreview?: string;
  titleAlignment?: 'center' | 'left';
  titleFont?: string;
  titleFontSize?: number;
  titleFontStyle?: string;
  titleFontWeight?: string | number;
  titleTextColor?: string;
  titleTextTransform?: 'capitalize';
}

interface Product {
  type?: string;
  available: boolean;
  barcodes: string[];
  best_selling_rank: number;
  body_html: string;
  collections: Array<{
    handle: string;
    id: number;
    sort_value: string;
    template_suffix: string;
    title: string;
  }>;
  compare_at_price_max: number | null;
  compare_at_price_min: number | null;
  created_at: string;
  extra_sort1: string | number | null;
  extra_sort2: string | number | null;
  extra_sort3: string | number | null;
  handle: string;
  html: {
    theme_id: number;
    value: string;
  };
  id: number;
  images: {
    [key: string]: string;
  };
  original_images?: Array<{
    alt?: string;
    height?: number;
    id?: number;
    position?: number;
    src?: string;
    width?: number;
  }>;
  images_info?: Array<{
    alt: string;
    height: number;
    id: number;
    position: number;
    src: string;
    width: number;
  }>;
  locations: Array<unknown>;
  metafields: Array<ProductMetaField>;
  options_with_values: Array<OptionsWithValues>;
  percent_sale_min: number | null;
  position: number | null;
  price_max: number;
  price_min: number;
  product_type: string;
  published_at: string;
  published_scope: string;
  review_count: number;
  review_ratings: number;
  skus: string[];
  tags?: string[];
  template_suffix: string;
  title: string;
  updated_at: string;
  variant_id?: number;
  variants: Array<ProductItemVariant>;
  vendor: string;
  weight_max: number | null;
  weight_min: number | null;
  original_tags?: string[];
}

interface ProductMetaField {
  type?: string;
  namespace?: string;
  key?: string;
  value?: string;
}

interface ProductItemVariant {
  available: boolean;
  barcode: string;
  compare_at_price: string | null;
  fullfillment_service: string;
  id: string;
  image: string | null;
  inventory_management: string;
  inventory_policy: string;
  inventory_quantity: number;
  merged_options: Array<string>;
  original_merged_options?: Array<string>;
  price: string;
  sku: string;
  title: string;
}

interface OptionsWithValues {
  values: Array<{ image: number | null; title: string; origin_title?: string }>;
  name: string;
  original_name?: string;
  label: string;
}
